import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  DataGridCustom,
} from '../../../components/index';

const containerStyle = {
  border: '1px solid #ccc',
  padding: 20,
  boxShadow: '0 0.1rem 0.2rem rgba(69, 90, 100, 0.12), 0 0.05rem 0.25rem rgba(69, 90, 100, 0.24)',
  background: '#fff',
  borderRadius: 0,
};

const barContainer = {
  position: 'relative',
  height: 15,
  background: '#dddddd',
  borderRadius: 13,
  overflow: 'hidden',
};

const periodText = {
  fontSize: 12,
  marginTop: 5,
  color: '#555',
};

const DezAnos = 10;

const timeDataInicioVazio = -2208977612000; // ano de 1900

const timeDataFimVazio = 10413802800000; // ano de 2300

class VisualizacaoPeriodos extends Component {
  constructor(props, context) {
    super(props, context);

    /**
     * Inicializa o estado do componente
     */
    this.state = this.getInitialState();
  }

  /**
   * Estado inicial:
   * - markers: marcações da timeline (anos + conflito)
   * - minTime/maxTime: escala do gráfico
   * - tooltip: controle do hover
   */
  getInitialState = () => ({
    markers: [],
    minTime: 0,
    maxTime: 0,
    tooltip: {
      visible: false,
      content: '',
      x: 0,
      y: 0,
      background: '#000',
    },
  });

  /**
   * Lifecycle: monta o gráfico ao iniciar
   */
  componentDidMount() {
    this.montarGrafico();
  }

  componentDidUpdate(prevProps) {
    if (this.props.parametros && this.props.parametros !== prevProps.parametros) {
      this.montarGrafico();
    }
  }

  /**
   * Calcula interseção entre dois períodos
   * Retorna null se não houver sobreposição
   */
  getIntersecao = (a, b) => {
    const inicio = Math.max(a.start, b.start);
    const fim = Math.min(a.end, b.end);

    if (inicio > fim) {
      return null;
    }

    return {
      inicio,
      fim,
    };
  };

  parsePeriodoDateInicio = (data1, data2) => {
    if (!data1 || data1 === '__/__/____') {
      // se não houver referência, fallback
      if (!data2 || data2 === '__/__/____') {
        return this.parseDate('01/01/1900').getTime();
      }

      const dataOriginal = this.parseDate(data2);

      const dataReferencia = new Date(
        dataOriginal.getFullYear() - DezAnos,
        0, // Janeiro
        1,
      );

      return dataReferencia.getTime();
    }

    return this.parseDate(data1).getTime();
  };

  parsePeriodoDateFim = (data1, data2) => {
    if (!data1 || data1 === '__/__/____') {
      // fallback caso também não exista referência
      if (!data2 || data2 === '__/__/____') {
        return this.parseDate('01/01/2300').getTime();
      }

      const dataOriginal = this.parseDate(data2);

      const dataReferencia = new Date(
        dataOriginal.getFullYear() + DezAnos,
        11, // Dezembro
        31,
      );

      return dataReferencia.getTime();
    }

    return this.parseDate(data1).getTime();
  };

  /**
   * Converte string "dd/mm/yyyy" para Date
   */
  parseDate = (dateStr) => {
    const [day, month, year] = dateStr.split('/');
    return new Date(year, month - 1, day);
  };

  /**
   * Monta os períodos que serão exibidos como barras
   */
  montarItems = () => {
    const {
      periodoReqConflito,
      periodoReqCorrente,
      protocoloCorrente,
      protocoloConflito,
      solicitanteCorrente,
      destinatarioCorrente,
      solicitanteConflito,
      destinatarioConflito,
    } = this.props.parametros;

    /**
     * Extrai datas de uma string "data - data"
     */
    const extrair = (periodo1, periodo2) => {
      const [inicio, fim] = periodo1.split(' - ');
      const [inicio2, fim2] = periodo2.split(' - ');
      const start = this.parsePeriodoDateInicio(inicio, inicio2);
      const end = this.parsePeriodoDateFim(fim, fim2);

      return {
        start,
        end,
        startDate: inicio,
        endDate: fim,
      };
    };

    const req1 = extrair(periodoReqCorrente, periodoReqConflito);
    const req2 = extrair(periodoReqConflito, periodoReqCorrente);

    return [
      {
        label: 'Requerimento Corrente',
        ...req1,
        protocolo: protocoloCorrente,
        solicitante: solicitanteCorrente,
        destinatario: destinatarioCorrente,
        color: '#2563eb',
      },
      {
        label: 'Requerimento Conflitante',
        ...req2,
        protocolo: protocoloConflito,
        solicitante: solicitanteConflito,
        destinatario: destinatarioConflito,
        color: '#16a34a',
      },
    ];
  };

  /**
   * Exibe tooltip ao passar o mouse sobre uma barra
   */
  handleMouseEnter = (event, item) => {
    const rect = event.currentTarget.getBoundingClientRect();

    // Calcula duração do período em dias
    // const dias = Math.ceil((item.end - item.start) / (1000 * 60 * 60 * 24));

    // const content = `${item.label}\nPeríodo: ${item.startDate} - ${item.endDate}`
    //  + `\nDuração: ${dias} dias${item.protocolo ? `\nProtocolo: ${item.protocolo}` : ''}`;

    const content = `${item.label}\nPeríodo: ${item.startDate} - ${item.endDate}`
    + `${item.protocolo ? `\nProtocolo: ${item.protocolo}` : ''}`;

    this.setState({
      tooltip: {
        visible: true,
        content,
        x: rect.left + (rect.width / 2),
        y: rect.top,
        background: item.color,
      },
    });
  };

  /**
   * Esconde tooltip
   */
  handleMouseLeave = () => {
    this.setState({
      tooltip: {
        visible: false,
        content: '',
        x: 0,
        y: 0,
        background: '#000',
      },
    });
  };

  /**
   * Monta toda a lógica do gráfico:
   * - calcula escala
   * - define anos
   * - adiciona conflito
   */
  montarGrafico = () => {
    const items = this.montarItems();

    // Descobre limites do período
    const starts = items
      .map(i => i.start)
      .filter(Number.isFinite);

    const ends = items
      .map(i => i.end)
      .filter(Number.isFinite);

    const rawMin = Math.min(...starts);
    const rawMax = Math.max(...ends);

    const startYear = new Date(rawMin).getFullYear();
    const endYear = new Date(rawMax).getFullYear();

    const minTime = new Date(startYear, 0, 1).getTime();
    const maxTime = rawMax;

    // Coleta anos únicos
    const yearsSet = new Set();
    items.forEach((item) => {
      yearsSet.add(new Date(item.start).getFullYear());
      yearsSet.add(new Date(item.end).getFullYear());
    });

    /**
     * Cria marcadores de ano
     */
    const markersList = Array.from(yearsSet).map(y => ({
      time: new Date(y, 0, 1).getTime(),
      label: String(y),
      isConflict: false,
      isLastYear: y === endYear,
    }));

    /**
     * Adiciona marcadores de conflito (início e fim)
     */
    /* const conflito = items.length === 2 ? this.getIntersecao(items[0], items[1]) : null;

    if (conflito) {
      const fmt = d => new Date(d).toLocaleDateString('pt-BR');

      markersList.push({
        time: conflito.inicio,
        label: fmt(conflito.inicio),
        isConflict: true,
        isStart: true,
      });

      markersList.push({
        time: conflito.fim,
        label: fmt(conflito.fim),
        isConflict: true,
        isEnd: true,
      });
    } */

    // Ordena cronologicamente
    markersList.sort((a, b) => a.time - b.time);

    this.setState({
      // markers: markersList,
      minTime,
      maxTime,
    });
  };

  /**
   * Converte tempo em posição percentual no gráfico (0 a 100%)
   */
  calcPos = (time) => {
    const { minTime, maxTime } = this.state;
    return ((time - minTime) / (maxTime - minTime)) * 100;
  };

  render() {
    const items = this.montarItems();

    /**
     * Calcula conflito novamente para uso no render
     */
    const conflito = items.length === 2 ? this.getIntersecao(items[0], items[1]) : null;

    const { tooltip } = this.state;
    const larguraConflito = conflito
      ? Math.max(
        0.8,
        this.calcPos(conflito.fim) - this.calcPos(conflito.inicio),
      )
      : 0;
    return (
      <React.Fragment>
        <DataGridCustom title="Painel de Períodos">
          <div style={containerStyle}>
            {/* BARRAS DOS PERÍODOS */}
            {items.map(item => (
              <div key={item.label} style={{ marginBottom: 25 }}>
                <div style={{ marginBottom: 5 }}>
                  <strong style={{ color: '#546e7a' }}>
                    {item.label}
                    {item.protocolo && ` | ${item.protocolo}`}
                    {item.solicitante && ` | Solicitante: ${item.solicitante}`}
                    {item.destinatario
                      && item.destinatario !== item.solicitante
                      && ` | Destinatário: ${item.destinatario}`}
                  </strong>
                </div>

                <div style={barContainer}>
                  <div
                    onMouseEnter={e => this.handleMouseEnter(e, item)}
                    onMouseLeave={this.handleMouseLeave}
                    style={{
                      position: 'absolute',
                      left: `${this.calcPos(item.start)}%`,
                      width: `${Math.max(
                        0,
                        this.calcPos(item.end) - this.calcPos(item.start),
                      )}%`,
                      height: '100%',
                      background: item.color,
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                  />
                </div>

                <div style={periodText}>{item.startDate} - {item.endDate}</div>
              </div>
            ))}

            {/* BARRA DO CONFLITO */}
            {conflito && (
              <div style={{ marginBottom: 25 }}>
                <div style={{ marginBottom: 5 }}>
                  <strong style={{ color: '#546e7a' }}>Período Conflitante</strong>
                </div>

                <div style={barContainer}>
                  <div
                    onMouseEnter={e => this.handleMouseEnter(e, {
                      label: 'Período Conflitante',
                      start: conflito.inicio,
                      end: conflito.fim,
                      startDate: conflito.inicio !== timeDataInicioVazio
                        ? new Date(conflito.inicio).toLocaleDateString()
                        : '__/__/____',
                      endDate: conflito.fim !== timeDataFimVazio
                        ? new Date(conflito.fim).toLocaleDateString()
                        : '__/__/____',
                      color: '#dc2626',
                    })}
                    onMouseLeave={this.handleMouseLeave}
                    style={{
                      position: 'absolute',
                      left: `${this.calcPos(conflito.inicio)}%`,
                      width: `${larguraConflito}%`,
                      height: '100%',
                      background: '#dc2626',
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                  />
                </div>

                <div style={periodText}>
                  {conflito.inicio !== timeDataInicioVazio ? new Date(conflito.inicio).toLocaleDateString() : '__/__/____'}
                  {' - '}
                  {conflito.fim !== timeDataFimVazio ? new Date(conflito.fim).toLocaleDateString() : '__/__/____'}
                </div>
              </div>
            )}

            {/* TOOLTIP */}
            {tooltip.visible && (
              <div
                style={{
                  position: 'fixed',
                  top: tooltip.y - 10,
                  left: tooltip.x,
                  transform: 'translate(-50%, -100%)',
                  background: tooltip.background,
                  color: '#fff',
                  padding: '8px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  whiteSpace: 'pre-line',
                  wordBreak: 'keep-all',
                  minWidth: 220,
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {tooltip.content}
              </div>
            )}
          </div>

          {/* LEGENDA */}
          <div style={{ marginTop: 20, display: 'flex', gap: 20 }}>
            {[
              { c: '#2563eb', l: 'Requerimento Corrente' },
              { c: '#16a34a', l: 'Requerimento Conflitante' },
              { c: '#dc2626', l: 'Período Conflitante' },
            ].map(leg => (
              <div key={leg.l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 12, height: 12, background: leg.c }} />
                <span style={{ fontSize: 12 }}>{leg.l}</span>
              </div>
            ))}
          </div>
        </DataGridCustom>
      </React.Fragment>
    );
  }
}

VisualizacaoPeriodos.propTypes = {
  parametros: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default VisualizacaoPeriodos;



// WEBPACK FOOTER //
// ./src/pages/requerimentos/Aposentadoria/VisualizacaoPeriodos.jsx